import React, { createContext, useContext, useState, useEffect } from 'react';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  const [isAccessibilityEnabled, setIsAccessibilityEnabled] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('niceice-accessibility-enabled');
    return saved === null ? true : JSON.parse(saved); // Default to enabled
  });

  const [accessibilityFeatures, setAccessibilityFeatures] = useState(() => {
    // Load saved features from localStorage
    const savedFeatures = localStorage.getItem('niceice-accessibility-features');
    return savedFeatures ? JSON.parse(savedFeatures) : {
      highContrast: false,
      largeText: false,
      reducedMotion: false,
      screenReaderOptimized: false,
      keyboardNavigation: true
    };
  });

  // Add text scale state
  const [textScale, setTextScale] = useState(() => {
    const saved = localStorage.getItem('niceice-text-scale');
    return saved ? parseInt(saved) : 100;
  });

  // Save accessibility preference to localStorage
  useEffect(() => {
    localStorage.setItem('niceice-accessibility-enabled', JSON.stringify(isAccessibilityEnabled));
  }, [isAccessibilityEnabled]);

  // Save accessibility features to localStorage
  useEffect(() => {
    localStorage.setItem('niceice-accessibility-features', JSON.stringify(accessibilityFeatures));
  }, [accessibilityFeatures]);

  // Save text scale to localStorage
  useEffect(() => {
    localStorage.setItem('niceice-text-scale', textScale.toString());
  }, [textScale]);

  // Apply accessibility classes to document - EXPERT IMPLEMENTATION
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;

    // Remove all accessibility classes first
    body.classList.remove(
      'accessibility-enabled', 
      'high-contrast', 
      'large-text', 
      'reduced-motion', 
      'screen-reader-optimized'
    );

    // Remove all text scale data attributes
    ['75', '100', '125', '150', '175', '200'].forEach(scale => {
      html.removeAttribute(`data-text-scale`);
    });

    // Apply base accessibility class if enabled (but this doesn't cause visual changes)
    if (isAccessibilityEnabled) {
      body.classList.add('accessibility-enabled');
      
      // Apply individual feature classes to BODY ONLY if features are enabled
      if (accessibilityFeatures.highContrast) {
        body.classList.add('high-contrast');
        console.log('✅ High contrast mode enabled');
      }

      if (accessibilityFeatures.reducedMotion) {
        body.classList.add('reduced-motion');
        console.log('✅ Reduced motion mode enabled');
      }

      if (accessibilityFeatures.screenReaderOptimized) {
        body.classList.add('screen-reader-optimized');
        console.log('✅ Screen reader optimized mode enabled');
      }

      // FIXED: Only apply text scaling if scale is different from 100% OR if largeText is enabled
      if (textScale !== 100 || accessibilityFeatures.largeText) {
        html.setAttribute('data-text-scale', textScale.toString());
        console.log(`✅ Text scale set to ${textScale}%`);
      }

      // Update large text feature based on scale
      const isLargeText = textScale > 100;
      if (accessibilityFeatures.largeText !== isLargeText) {
        setAccessibilityFeatures(prev => ({
          ...prev,
          largeText: isLargeText
        }));
      }
    }

    // Set ARIA attribute for screen readers
    html.setAttribute('data-accessibility-enabled', isAccessibilityEnabled.toString());
    
    // Set dir attribute to ensure RTL is maintained
    html.setAttribute('dir', 'rtl');
    
    // Apply reduced motion at CSS level for better browser support
    if (accessibilityFeatures.reducedMotion) {
      // Add CSS to disable animations globally
      let reducedMotionStyle = document.getElementById('reduced-motion-style');
      if (!reducedMotionStyle) {
        reducedMotionStyle = document.createElement('style');
        reducedMotionStyle.id = 'reduced-motion-style';
        document.head.appendChild(reducedMotionStyle);
      }
      
      reducedMotionStyle.textContent = `
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
          transform: none !important;
        }
        .animate-pulse, .animate-fade-in, .animate-slide-in, .animate-pulse-slow,
        [class*="animate-"], [class*="transition-"], 
        .hover\\:scale-, .hover\\:-translate-y-, .hover\\:shadow- {
          animation: none !important;
          transition: none !important;
          transform: none !important;
        }
      `;
    } else {
      // Remove reduced motion styles when disabled
      const reducedMotionStyle = document.getElementById('reduced-motion-style');
      if (reducedMotionStyle) {
        reducedMotionStyle.remove();
      }
    }
    
    // Log current classes for debugging
    console.log('🔧 Applied accessibility classes:', {
      enabled: isAccessibilityEnabled,
      features: accessibilityFeatures,
      textScale: textScale,
      bodyClasses: Array.from(body.classList).filter(cls => 
        cls.includes('accessibility') || 
        cls.includes('contrast') || 
        cls.includes('text') || 
        cls.includes('motion') || 
        cls.includes('reader')
      ),
      htmlAttributes: {
        'data-text-scale': html.getAttribute('data-text-scale'),
        'data-accessibility-enabled': html.getAttribute('data-accessibility-enabled')
      }
    });

  }, [isAccessibilityEnabled, accessibilityFeatures, textScale]);

  const toggleAccessibility = () => {
    const newState = !isAccessibilityEnabled;
    setIsAccessibilityEnabled(newState);
    
    // Announce the change
    announceChange(`נגישות ${newState ? 'הופעלה' : 'הושבתה'}`);
  };

  const updateAccessibilityFeature = (feature, value) => {
    setAccessibilityFeatures(prev => {
      const newFeatures = {
        ...prev,
        [feature]: value
      };
      
      // Log the update for debugging
      console.log(`🔧 Accessibility feature updated: ${feature} = ${value}`, newFeatures);
      
      // Announce the change based on feature
      const featureNames = {
        highContrast: 'ניגודיות גבוהה',
        largeText: 'טקסט מוגדל',
        reducedMotion: 'צמצום אנימציות',
        screenReaderOptimized: 'אופטימיזציה לקורא מסך'
      };
      
      const announcement = `${featureNames[feature]} ${value ? 'הופעל' : 'הושבת'}`;
      announceChange(announcement);
      
      return newFeatures;
    });
  };

  const updateTextScale = (newScale) => {
    // Clamp scale between 75% and 200%
    const clampedScale = Math.max(75, Math.min(200, newScale));
    setTextScale(clampedScale);
    
    // Update large text feature based on scale
    const isLargeText = clampedScale > 100;
    setAccessibilityFeatures(prev => ({
      ...prev,
      largeText: isLargeText
    }));
    
    // Announce the change
    announceChange(`גודל טקסט שונה ל-${clampedScale}%`);
    
    console.log(`🔧 Text scale updated to ${clampedScale}%`);
  };

  const resetAccessibilityFeatures = () => {
    const defaultFeatures = {
      highContrast: false,
      largeText: false,
      reducedMotion: false,
      screenReaderOptimized: false,
      keyboardNavigation: true
    };
    
    setAccessibilityFeatures(defaultFeatures);
    setTextScale(100);
    announceChange('הגדרות נגישות אופסו');
    console.log('🔄 Accessibility features reset to defaults');
  };

  // Helper function to announce changes to screen readers
  const announceChange = (message) => {
    // Create temporary announcement element
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = message;
    document.body.appendChild(announcer);
    
    // Remove after announcement
    setTimeout(() => {
      if (document.body.contains(announcer)) {
        document.body.removeChild(announcer);
      }
    }, 1000);
  };

  const value = {
    isAccessibilityEnabled,
    accessibilityFeatures,
    textScale,
    toggleAccessibility,
    updateAccessibilityFeature,
    updateTextScale,
    resetAccessibilityFeatures,
    setIsAccessibilityEnabled
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityContext;